/**
 * PolicyAcknowledgmentPage - Business User page to view and acknowledge BCM policies
 * ISO 22301 Clause 5.2 - Policy
 * Annual acknowledgment required for all approved policies
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Eye,
  Shield,
  Calendar,
  RefreshCw,
  Award,
  Target,
  Sparkles,
} from "lucide-react";

const PDCA_TABS = [
  { id: "all", label: "ALL", color: "text-slate-600", bg: "bg-slate-100", active: "bg-slate-800 text-white" },
  { id: "plan", label: "PLAN", color: "text-blue-600", bg: "bg-blue-50", active: "bg-blue-600 text-white" },
  { id: "do", label: "DO", color: "text-emerald-600", bg: "bg-emerald-50", active: "bg-emerald-600 text-white" },
  { id: "check", label: "CHECK", color: "text-amber-600", bg: "bg-amber-50", active: "bg-amber-600 text-white" },
  { id: "act", label: "ACT", color: "text-purple-600", bg: "bg-purple-50", active: "bg-purple-600 text-white" },
];

const PDCA_BORDER_MAP = {
  plan: "border-l-blue-500",
  do: "border-l-emerald-500",
  check: "border-l-amber-500",
  act: "border-l-purple-500",
};

const classifyPolicyPDCA = (policyType) => {
  const t = (policyType || '').toLowerCase();
  if (t.includes('plan') || t.includes('scope') || t.includes('context') || t.includes('risk') || t.includes('bia')) return 'plan';
  if (t.includes('do') || t.includes('implement') || t.includes('resource') || t.includes('awareness') || t.includes('operation')) return 'do';
  if (t.includes('check') || t.includes('monitor') || t.includes('audit') || t.includes('review') || t.includes('exercise')) return 'check';
  if (t.includes('act') || t.includes('improve') || t.includes('correct') || t.includes('update')) return 'act';
  return 'plan'; // default
};

export default function PolicyAcknowledgmentPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ pending: [], acknowledged: [], total_policies: 0 });
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyContent, setPolicyContent] = useState(null);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [hasRead, setHasRead] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [pdcaFilter, setPdcaFilter] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch pending acknowledgments (workflow-based) 
      const pendingResponse = await api.get("/policies/acknowledgments/pending");
      
      // Also fetch my-pending which includes approved policies without workflow
      const myPendingResponse = await api.get("/policies/acknowledgments/my-pending");
      
      // Combine pending from both sources
      const workflowPending = pendingResponse.data.pending_acknowledgments || [];
      const policyPending = myPendingResponse.data.pending || [];
      const acknowledged = myPendingResponse.data.acknowledged || [];
      
      setData({
        pending: workflowPending.length > 0 ? workflowPending : policyPending,
        pending_count: workflowPending.length > 0 ? workflowPending.length : policyPending.length,
        acknowledged: acknowledged,
        acknowledged_count: acknowledged.length,
        total_policies: (workflowPending.length > 0 ? workflowPending.length : policyPending.length) + acknowledged.length
      });
    } catch (error) {
      console.error("Failed to fetch acknowledgments:", error);
      toast.error("Failed to load policy acknowledgments");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const viewPolicy = async (policyAck) => {
    setSelectedPolicy(policyAck);
    setHasRead(false);
    
    try {
      // Fetch full policy content using policy_id (or id for my-pending endpoint)
      const policyId = policyAck.policy_id || policyAck.id;
      const response = await api.get(`/policies/${policyId}`);
      setPolicyContent(response.data);
      setShowPolicyDialog(true);
    } catch (error) {
      console.error("Failed to fetch policy:", error);
      toast.error("Failed to load policy content");
    }
  };

  const acknowledgePolicy = async () => {
    if (!hasRead || !selectedPolicy) return;
    
    setAcknowledging(true);
    try {
      // Use acknowledgment_id if available (workflow-based), otherwise use policy_id directly
      if (selectedPolicy.acknowledgment_id) {
        await api.post(`/policies/acknowledgments/${selectedPolicy.acknowledgment_id}/acknowledge`);
      } else {
        // Direct acknowledgment using policy_id
        await api.post(`/policies/acknowledgments/${selectedPolicy.policy_id || selectedPolicy.id}`);
      }
      toast.success(`Policy "${selectedPolicy.policy_title || selectedPolicy.title}" acknowledged!`);
      setShowPolicyDialog(false);
      setSelectedPolicy(null);
      setPolicyContent(null);
      fetchData(); // Refresh the list
    } catch (error) {
      console.error("Failed to acknowledge policy:", error);
      toast.error("Failed to acknowledge policy");
    } finally {
      setAcknowledging(false);
    }
  };

  const completionRate = data.total_policies > 0 
    ? Math.round((data.acknowledged_count / data.total_policies) * 100) 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="policy-acknowledgment-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Badge className="bg-sky-100 text-sky-700 text-sm px-3 py-1">PP1</Badge>
            <Badge className="bg-amber-100 text-amber-700 text-sm">Annual Requirement</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Policy Acknowledgment</h1>
          <p className="text-slate-600 mt-1">Review and acknowledge BCM policies to maintain compliance</p>
        </div>
        <Button variant="outline" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      {/* Progress — Compact Mockup Style */}
      <div className={`border rounded-lg p-5 ${completionRate === 100 ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${completionRate === 100 ? "bg-emerald-500" : "bg-amber-500"}`}>
              {completionRate === 100 ? <Award className="w-5 h-5 text-white" /> : <Target className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{completionRate === 100 ? "All Policies Acknowledged!" : "Policies Pending"}</h3>
              <p className="text-xs text-slate-500">{data.acknowledged_count} of {data.total_policies} policies acknowledged</p>
            </div>
          </div>
          <div className={`text-3xl font-extrabold ${completionRate === 100 ? "text-emerald-600" : "text-amber-600"}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{completionRate}%</div>
        </div>
        <Progress value={completionRate} className="h-2" />
      </div>

      {/* PDCA Filter Tabs — Mockup Style */}
      <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg p-1" data-testid="pdca-filter-tabs">
        {PDCA_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setPdcaFilter(tab.id)}
            className={`px-4 py-1.5 rounded-md text-[0.6875rem] font-bold tracking-wider transition-all ${pdcaFilter === tab.id ? tab.active : `${tab.bg} ${tab.color} hover:opacity-80`}`}
            data-testid={`pdca-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Pending Policies — Left-border Card Style */}
      {data.pending.filter(p => pdcaFilter === 'all' || classifyPolicyPDCA(p.policy_type) === pdcaFilter).length > 0 && (
        <div>
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Policies Requiring Acknowledgment ({data.pending.filter(p => pdcaFilter === 'all' || classifyPolicyPDCA(p.policy_type) === pdcaFilter).length})
          </h2>
          <div className="grid gap-3">
            {data.pending.filter(p => pdcaFilter === 'all' || classifyPolicyPDCA(p.policy_type) === pdcaFilter).map((policy) => {
              const phase = classifyPolicyPDCA(policy.policy_type);
              return (
                <div key={policy.acknowledgment_id || policy.policy_id} className={`bg-white border border-slate-200 rounded-lg p-4 border-l-4 ${PDCA_BORDER_MAP[phase] || 'border-l-sky-700'} hover:shadow-sm transition-all`} data-testid={`pending-policy-${policy.acknowledgment_id || policy.policy_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3.5">
                      <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5"><FileText className="w-4 h-4 text-amber-600" /></div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-800">{policy.policy_title || policy.title}</h3>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{policy.description}</p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <Badge variant="outline" className="text-[10px] bg-white">{phase.toUpperCase()}</Badge>
                          <span className="text-[10px] text-slate-400 flex items-center gap-1"><Shield className="w-3 h-3" />{policy.policy_type?.replace(/_/g, ' ') || 'BCM Policy'}</span>
                          {policy.requested_at && <span className="text-[10px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(policy.requested_at).toLocaleDateString()}</span>}
                        </div>
                        {policy.renewal_required && <Badge className="mt-2 bg-red-100 text-red-700 text-[10px]">Renewal Required</Badge>}
                        {policy.never_acknowledged && <Badge className="mt-2 bg-amber-100 text-amber-700 text-[10px]">Never Acknowledged</Badge>}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => viewPolicy(policy)} className="flex-shrink-0" data-testid={`view-policy-${policy.acknowledgment_id || policy.policy_id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1.5" />Read & Acknowledge
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Acknowledged Policies — Left-border Card Style */}
      {data.acknowledged.filter(p => pdcaFilter === 'all' || classifyPolicyPDCA(p.policy_type) === pdcaFilter).length > 0 && (
        <div>
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            Acknowledged ({data.acknowledged.filter(p => pdcaFilter === 'all' || classifyPolicyPDCA(p.policy_type) === pdcaFilter).length})
          </h2>
          <div className="grid gap-3">
            {data.acknowledged.filter(p => pdcaFilter === 'all' || classifyPolicyPDCA(p.policy_type) === pdcaFilter).map((policy) => {
              const phase = classifyPolicyPDCA(policy.policy_type);
              return (
                <div key={policy.id} className={`bg-white border border-slate-200 rounded-lg p-4 border-l-4 ${PDCA_BORDER_MAP[phase] || 'border-l-sky-700'} opacity-80`} data-testid={`acked-policy-${policy.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3.5">
                      <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-800">{policy.title}</h3>
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
                          <Badge variant="outline" className="text-[10px] bg-white">{phase.toUpperCase()}</Badge>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Ack: {new Date(policy.acknowledged_at).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Renewal in {policy.days_until_renewal}d</span>
                        </div>
                      </div>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />Done</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {data.total_policies === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg py-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No Policies Available</h3>
          <p className="text-slate-500 mt-1">There are no approved policies requiring acknowledgment at this time.</p>
        </div>
      )}

      {/* Policy View Dialog */}
      <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-sky-700" />
              {selectedPolicy?.policy_title || selectedPolicy?.title}
            </DialogTitle>
            <DialogDescription>
              Please read the entire policy before acknowledging
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 overflow-y-auto py-4 max-h-[50vh]">
            {policyContent && (
              <div className="prose prose-sm prose-slate max-w-none">
                <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Policy Type:</span>
                      <span className="ml-2 font-medium">{policyContent.policy_type?.replace(/_/g, ' ')}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Version:</span>
                      <span className="ml-2 font-medium">{policyContent.version}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Effective Date:</span>
                      <span className="ml-2 font-medium">
                        {policyContent.effective_date ? new Date(policyContent.effective_date).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Status:</span>
                      <Badge className="ml-2 bg-emerald-100 text-emerald-700">{policyContent.status}</Badge>
                    </div>
                  </div>
                </div>
                
                <div className="whitespace-pre-wrap text-slate-700">
                  {policyContent.content}
                </div>
              </div>
            )}
          </ScrollArea>

          <div className="border-t pt-4">
            <div className="flex items-center space-x-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Checkbox 
                id="confirm-read" 
                checked={hasRead} 
                onCheckedChange={setHasRead}
                data-testid="confirm-read-checkbox"
              />
              <label
                htmlFor="confirm-read"
                className="text-sm font-medium text-slate-700 cursor-pointer"
              >
                I have read and understood this policy. I acknowledge my responsibility to comply with its requirements.
              </label>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPolicyDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={acknowledgePolicy}
                disabled={!hasRead || acknowledging}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="acknowledge-btn"
              >
                {acknowledging ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Acknowledge Policy
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
